import html2canvas from 'html2canvas';
import { formatDateForSharing } from '../../utils/shareUtils';

/**
 * 圖片下載工具 V2（重構版）
 * 核心原則：不使用 onclone 覆蓋任何樣式，讓 html2canvas 直接捕獲原始渲染
 */

const isIOSDevice = () =>
  /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

const generateFileName = ({ calculatedDays, relationshipLabel, anniversaryDate, language }) => {
  const dateStr = anniversaryDate
    ? formatDateForSharing(anniversaryDate, language).replace(/[\/\s年月日]/g, '-')
    : '';
  const typeStr = relationshipLabel
    ? relationshipLabel.replace(/\s+/g, '-').toLowerCase()
    : 'love';
  return `${typeStr}-${calculatedDays}-days${dateStr ? '-' + dateStr : ''}.png`;
};

const handleIOSDownload = async (canvas, fileName, language) => {
  return new Promise((resolve, reject) => {
    canvas.toBlob(async (blob) => {
      if (!blob) { reject(new Error('無法生成圖片')); return; }
      try {
        if (navigator.share && navigator.canShare) {
          const file = new File([blob], fileName, { type: 'image/png' });
          if (navigator.canShare({ files: [file] })) {
            await navigator.share({
              title: language === 'zh' ? '我們的愛情時光' : 'Our Love Journey',
              files: [file]
            });
            resolve(); return;
          }
        }
        const url = URL.createObjectURL(blob);
        const newWindow = window.open('', '_blank');
        if (newWindow) {
          newWindow.document.write(`
            <!DOCTYPE html><html>
            <head>
              <title>${language === 'zh' ? '長按保存圖片' : 'Long press to save'}</title>
              <meta name="viewport" content="width=device-width, initial-scale=1.0">
              <style>
                body{margin:0;padding:20px;background:#f3f4f6;font-family:system-ui,sans-serif;text-align:center;min-height:100vh;display:flex;flex-direction:column;justify-content:center;align-items:center;}
                .box{background:white;padding:24px;border-radius:16px;margin-bottom:20px;box-shadow:0 4px 20px rgba(0,0,0,0.1);max-width:360px;}
                h3{margin:0 0 12px;color:#1f2937;font-size:18px;}
                p{margin:0;color:#6b7280;font-size:14px;line-height:1.5;}
                img{max-width:300px;height:auto;border-radius:16px;box-shadow:0 8px 32px rgba(0,0,0,0.15);}
                .close{position:fixed;top:20px;right:20px;background:rgba(0,0,0,0.8);color:white;border:none;border-radius:50%;width:44px;height:44px;font-size:24px;cursor:pointer;}
              </style>
            </head>
            <body>
              <button class="close" onclick="window.close()">&times;</button>
              <div class="box">
                <h3>${language === 'zh' ? '📱 保存到相簿' : '📱 Save to Photos'}</h3>
                <p>${language === 'zh' ? '長按下方圖片，選擇「儲存到照片」' : 'Long press the image, then select "Save to Photos"'}</p>
              </div>
              <img src="${url}" alt="Love Anniversary Image" />
            </body></html>
          `);
        }
        setTimeout(() => URL.revokeObjectURL(url), 60000);
        resolve();
      } catch (error) { reject(error); }
    }, 'image/png', 1.0);
  });
};

const handleStandardDownload = (canvas, fileName) => {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) { reject(new Error('無法生成圖片')); return; }
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      setTimeout(() => { document.body.removeChild(link); URL.revokeObjectURL(url); }, 100);
      resolve();
    }, 'image/png', 1.0);
  });
};

/**
 * 高品質圖片下載主函數
 * @param {Object} params
 */
export const downloadPerfectImage = async ({
  template,
  calculatedDays,
  relationshipLabel,
  anniversaryDate,
  anniversaryType,
  language
}) => {
  const downloadElement = document.getElementById('perfect-download-canvas');
  if (!downloadElement) throw new Error('下載畫布未找到');

  // 等待 SVG gradient 和字體完全渲染（縮短至 1200ms）
  await new Promise(resolve => setTimeout(resolve, 1200));

  try {
    const canvas = await html2canvas(downloadElement, {
      backgroundColor: null,
      scale: 1,
      width: 1080,
      height: 1350,
      useCORS: true,
      allowTaint: true,
      foreignObjectRendering: false,
      logging: false,
      scrollX: 0,
      scrollY: 0,
      windowWidth: 1920,
      windowHeight: 2400,
      removeContainer: false,
      imageTimeout: 15000
    });

    const fileName = generateFileName({ calculatedDays, relationshipLabel, anniversaryDate, language });

    if (isIOSDevice()) {
      await handleIOSDownload(canvas, fileName, language);
    } else {
      await handleStandardDownload(canvas, fileName);
    }
  } catch (error) {
    console.error('圖片下載失敗:', error);
    throw new Error(language === 'zh' ? '圖片生成失敗，請重試' : 'Failed to generate image, please try again');
  }
};